import React from 'react';

import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { ThemeProvider, useTheme } from '@mui/material/styles';

import type { CompanyBrandProfile } from '../branding/companyBrand';
import {
  getCompanyBrandCssVars,
  resolveCompanyBrandFontStack,
} from '../branding/companyBrand';

interface CompanyBrandProviderProps {
  brand: CompanyBrandProfile;
  children: React.ReactNode;
  className?: string;
}

export type CompanyBrandColorScheme = 'light' | 'dark';
const CompanyBrandColorSchemeContext = React.createContext<CompanyBrandColorScheme>('light');

export function useCompanyBrandColorScheme(): CompanyBrandColorScheme {
  return React.useContext(CompanyBrandColorSchemeContext);
}

function isDarkColor(value: string): boolean {
  const match = value.trim().match(/^#([\da-f]{6})$/i);
  if (!match) return false;
  const rgb = [0, 2, 4].map((offset) => parseInt(match[1].slice(offset, offset + 2), 16));
  const luminance = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  return luminance < 0.42;
}

const CompanyBrandProvider: React.FC<CompanyBrandProviderProps> = ({
  brand,
  children,
  className = 'company-branded-preview',
}) => {
  const fontFamily = resolveCompanyBrandFontStack(brand.fontFamily);
  const colorScheme: CompanyBrandColorScheme = isDarkColor(brand.background) ? 'dark' : 'light';
  const outerTheme = useTheme<Theme>();

  React.useEffect(() => {
    const previousBodyBackground = document.body.style.background;
    const previousHtmlBackground = document.documentElement.style.background;
    document.body.style.background = brand.background;
    document.documentElement.style.background = brand.background;

    return () => {
      document.body.style.background = previousBodyBackground;
      document.documentElement.style.background = previousHtmlBackground;
    };
  }, [brand.background]);

  const brandedTheme = React.useMemo<Theme>(
    () => ({
      ...outerTheme,
      typography: { ...outerTheme.typography, fontFamily },
    }),
    [fontFamily, outerTheme]
  );

  return (
    <CompanyBrandColorSchemeContext.Provider value={colorScheme}>
      <ThemeProvider theme={brandedTheme}>
        <Box
          className={className}
          data-cx-color-scheme={colorScheme}
          style={getCompanyBrandCssVars(brand)}
          sx={{
            fontFamily,
            '& .MuiButtonBase-root, & .MuiInputBase-root, & .MuiTypography-root': {
              fontFamily: 'inherit',
            },
          }}
        >
          {children}
        </Box>
      </ThemeProvider>
    </CompanyBrandColorSchemeContext.Provider>
  );
};

export default CompanyBrandProvider;
